import { useEffect, type ClipboardEvent, type KeyboardEvent, type ReactNode } from "react";
import {
  FieldArrayWithId,
  FieldErrors,
  FieldNamesMarkedBoolean,
  UseFormClearErrors,
  UseFormRegister,
  UseFormResetField,
  UseFormSetValue,
} from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CodedInput } from "@/modules/add-new-data/components/coded-input";
import { DateOfBirthInput } from "@/modules/add-new-data/components/date-of-birth-input";
import { ScrollableFormShell } from "@/modules/add-new-data/components/scrollable-form-shell";
import {
  calculateAgeDetailsFromBirthdate,
  formatAgeAsOfDate,
  getAgeEntryModeValues,
  parseMMDDYYYY,
  resolveMemberAge,
} from "@/modules/add-new-data/lib/age";
import {
  getApplicabilityForQuestion,
  type MemberFieldName,
} from "@/modules/add-new-data/lib/cleansing";
import {
  AddNewDataFormValues,
  emptyMember,
} from "@/modules/add-new-data/lib/schema";
import { isHouseholdMemberRowBlank } from "@/modules/add-new-data/lib/household-member-rows";
import { checkRelationshipSexWarning } from "@/modules/add-new-data/lib/validation";
import { cleanRBIMCode } from "@/lib/rbimCodeMap";
import { cn } from "@/lib/utils";

type RbimDemographicPagesStepProps = {
  fields: FieldArrayWithId<AddNewDataFormValues, "members", "id">[];
  errors: FieldErrors<AddNewDataFormValues>;
  family: AddNewDataFormValues["household"]["family"];
  submitCount: number;
  touchedFields: FieldNamesMarkedBoolean<AddNewDataFormValues>;
  watchedMembers: AddNewDataFormValues["members"];
  clearErrors: UseFormClearErrors<AddNewDataFormValues>;
  register: UseFormRegister<AddNewDataFormValues>;
  resetField: UseFormResetField<AddNewDataFormValues>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
  ageReferenceDate: string;
  append: (member: typeof emptyMember) => void;
  remove: (index: number) => void;
  addMemberLabel?: string;
  minimumMemberCount?: number;
  onAddMember?: () => void;
};

const cellInput =
  "h-8 rounded-none border-0 bg-white px-1.5 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:ring-offset-0";

export function RbimDemographicPagesStep({
  fields,
  errors,
  family,
  clearErrors,
  watchedMembers,
  register,
  resetField,
  setValue,
  ageReferenceDate,
  append,
  remove,
  submitCount,
  touchedFields,
  addMemberLabel = "Add Line",
  minimumMemberCount = 1,
  onAddMember,
}: RbimDemographicPagesStepProps) {
  const membersError =
    errors.members && !Array.isArray(errors.members) && "message" in errors.members
      ? String(errors.members.message)
      : undefined;

  const removeLastMember = () => {
    const lastIndex = fields.length - 1;
    const lastMember = watchedMembers[lastIndex];

    if (lastIndex < minimumMemberCount || (
      !isHouseholdMemberRowBlank(lastMember) &&
      !window.confirm(`Line ${lastIndex + 1} contains encoded data. Remove it?`)
    )) {
      return;
    }

    remove(lastIndex);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-4 shadow-sm">
        <div>
          <p className="text-sm font-semibold text-slate-950">Pages 2 - 3: Demographic Characteristics</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Continuous RBIM encoding grid. Scroll horizontally to complete Q1 to Q15.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (onAddMember) {
                onAddMember();
              } else {
                append({ ...emptyMember });
              }
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            {addMemberLabel}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={fields.length <= minimumMemberCount}
            onClick={removeLastMember}
          >
            <Trash2 className="size-4" aria-hidden="true" />
            Remove Last
          </Button>
        </div>
      </div>

      {membersError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm font-medium text-destructive">
          {membersError}
        </div>
      )}

      <FamilyAssignmentEditor
        errors={errors}
        family={family}
        fields={fields}
        register={register}
        setValue={setValue}
      />

      <ScrollableFormShell minWidthClassName="min-w-[2880px]">
          <table className="w-full border-collapse text-center text-xs text-slate-950" onKeyDown={handleDemographicGridKeyDown}>
            <thead>
              <tr>
                <th rowSpan={4} className="w-[64px] border border-slate-700 bg-[#eeeeee] px-2 py-2 align-bottom font-bold">
                  LINE<br />LIST<br />NO.
                </th>
                <th colSpan={15} className="border border-slate-700 bg-[#eeeeee] px-2 py-2 text-base font-extrabold uppercase">
                  A. Demographic Characteristics
                </th>
              </tr>
              <tr>
                <th colSpan={10} className="border border-slate-700 bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                  For All Household Members
                </th>
                <th className="border border-slate-700 bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                  For 5 Yrs. & Above
                </th>
                <th colSpan={4} className="border border-slate-700 bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                  For 3-24 Years Old
                </th>
              </tr>
              <tr>
                <QuestionHeader className="w-[460px]" code="Q1" title="Name">
                  Surname, First Name, Middle Name or Middle Initial
                </QuestionHeader>
                <QuestionHeader className="w-[170px]" code="Q2" title="Relationship to HHH" />
                <QuestionHeader className="w-[110px]" code="Q3" title="Sex" />
                <QuestionHeader className="w-[250px]" code="Q4" title="Age" />
                <QuestionHeader className="w-[220px]" code="Q5" title="Date of Birth">
                  MM/DD/YYYY
                </QuestionHeader>
                <QuestionHeader className="w-[170px]" code="Q6" title="Place of Birth" />
                <QuestionHeader className="w-[145px]" code="Q7" title="Nationality" />
                <QuestionHeader className="w-[150px]" code="Q8" title="Marital Status" />
                <QuestionHeader className="w-[165px]" code="Q9" title="Religion" />
                <QuestionHeader className="w-[170px]" code="Q10" title="Ethnicity" />
                <QuestionHeader className="w-[190px]" code="Q11" title="Highest Level of Education" />
                <QuestionHeader className="w-[160px]" code="Q12" title="Currently Enrolled" />
                <QuestionHeader className="w-[165px]" code="Q13" title="School Level" />
                <QuestionHeader className="w-[190px]" code="Q14" title="Place of School" />
                <QuestionHeader className="w-[240px]" code="Q15" title="Reason for Not Attending School" />
              </tr>
              <tr>
                <Instruction>Who are the members of this household starting from the HH head?</Instruction>
                <Instruction>What is ___'s relationship to HH head?</Instruction>
                <Instruction>Is ___ male or female?</Instruction>
                <Instruction>How old is _____ as of his/her last birthday?</Instruction>
                <Instruction>When was ___ born?</Instruction>
                <Instruction>Where was ___ born?<br />City or Municipality and Province</Instruction>
                <Instruction>Is ___ a Filipino? If not, what is ___'s nationality?</Instruction>
                <Instruction>What is ___'s current marital status?</Instruction>
                <Instruction>What is the religion of ___?</Instruction>
                <Instruction>What is ___'s ethnicity?</Instruction>
                <Instruction>What is the highest level of education completed by ___?</Instruction>
                <Instruction>Is ___ currently enrolled?</Instruction>
                <Instruction>What is the current school level of _____?</Instruction>
                <Instruction>In what barangay and city/municipality is ___ currently attending school?</Instruction>
                <Instruction>What is the reason why ___ is not attending school?</Instruction>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) =>
                renderCombinedRow(
                  field,
                  index,
                  errors,
                  touchedFields,
                  submitCount,
                  watchedMembers,
                  clearErrors,
                  register,
                  resetField,
                  setValue,
                  ageReferenceDate,
                  remove,
                  fields.length,
                  minimumMemberCount,
                ),
              )}
            </tbody>
          </table>
      </ScrollableFormShell>

      
    </div>
  );
}

function renderCombinedRow(
  field: FieldArrayWithId<AddNewDataFormValues, "members", "id">,
  index: number,
  errors: FieldErrors<AddNewDataFormValues>,
  touchedFields: FieldNamesMarkedBoolean<AddNewDataFormValues>,
  submitCount: number,
  watchedMembers: AddNewDataFormValues["members"],
  clearErrors: UseFormClearErrors<AddNewDataFormValues>,
  register: UseFormRegister<AddNewDataFormValues>,
  resetField: UseFormResetField<AddNewDataFormValues>,
  setValue: UseFormSetValue<AddNewDataFormValues>,
  ageReferenceDate: string,
  remove: (index: number) => void,
  fieldCount: number,
  minimumMemberCount: number,
) {
  const memberErrors = errors.members?.[index];
  const showDateOfBirthError = Boolean(memberErrors?.dateOfBirth?.message);
  const showReportedAgeError = Boolean(memberErrors?.reportedAge?.message);
  const member = watchedMembers[index];
  const referenceDate = parseMMDDYYYY(ageReferenceDate) ?? new Date();
  const resolvedAge = resolveMemberAge(
    member ?? {},
    member?.birthdateSource === "legacy-import" ? undefined : referenceDate,
  );
  const detailedAge = resolvedAge.precision === "exact"
    ? calculateAgeDetailsFromBirthdate(resolvedAge.compatibilityDate, referenceDate)?.displayAge ?? null
    : null;
  const relationshipSexWarning = checkRelationshipSexWarning(
    watchedMembers[index]?.relationshipToHead,
    watchedMembers[index]?.sex,
  );
  const q15State = getReasonNotAttendingState(member);
  const q15Error = memberErrors?.reasonNotAttendingSchool?.message;
  const q15OtherError = memberErrors?.reasonNotAttendingSchoolOther?.message;
  const q15 = cleanRBIMCode("q15_reason_not_attending_school", member?.reasonNotAttendingSchool);
  const q15HasInlineOther = /^0*20(?:\s*[-,:]\s*|\s+).+/i.test(String(member?.reasonNotAttendingSchool ?? "").trim());
  const q11Skipped = isQuestionSkipped(member, "highestEducation");
  const q12Skipped = isQuestionSkipped(member, "currentlyEnrolled");
  const q13Skipped = isQuestionSkipped(member, "schoolLevel");
  const q14Skipped = isQuestionSkipped(member, "placeOfSchool");
  const nationality = cleanRBIMCode("q07_nationality", member?.nationality);
  const showNationalityOther =
    !nationality.error &&
    nationality.code === "2" &&
    !/^0*2(?:\s*[-,:]\s*|\s+).+/i.test(String(member?.nationality ?? "").trim());

  return (
    <tr key={field.id} className="h-[52px]">
      <td className="border border-slate-700 bg-[#eeeeee] align-middle">
        <div className="flex items-center justify-center gap-1 px-1">
          <span className="text-base font-bold">{index + 1}</span>
          <button
            type="button"
            className="rounded p-1 text-slate-500 hover:bg-white hover:text-destructive disabled:pointer-events-none disabled:opacity-30"
            aria-label={`Remove line ${index + 1}`}
            disabled={fieldCount === 1 || index < minimumMemberCount}
            onClick={() => remove(index)}
          >
            <Trash2 className="size-3" aria-hidden="true" />
          </button>
        </div>
      </td>
      <DataCell hasError={Boolean(memberErrors?.firstName?.message || memberErrors?.lastName?.message)}>
        <div className="grid grid-cols-3 gap-px bg-white p-1">
          <Input autoUppercase className={cellInput} data-grid-input="true" placeholder="Surname" {...register(`members.${index}.lastName`)} />
          <Input autoUppercase className={cellInput} data-grid-input="true" placeholder="First name" {...register(`members.${index}.firstName`)} />
          <Input autoUppercase className={cellInput} data-grid-input="true" placeholder="Middle name" {...register(`members.${index}.middleName`)} />
        </div>
        {(memberErrors?.firstName?.message || memberErrors?.lastName?.message) && (
          <ErrorText>{memberErrors.firstName?.message ?? memberErrors.lastName?.message}</ErrorText>
        )}
      </DataCell>
      <DataCell
        hasError={Boolean(memberErrors?.relationshipToHead?.message)}
        hasWarning={Boolean(relationshipSexWarning && !memberErrors?.relationshipToHead?.message)}
      >
        <CodedInput
          className={cellInput}
          data-grid-input="true"
          questionKey="q02_relationship_to_head"
          registration={register(`members.${index}.relationshipToHead`)}
          value={watchedMembers[index]?.relationshipToHead}
        />
        {memberErrors?.relationshipToHead?.message && <ErrorText>{memberErrors.relationshipToHead.message}</ErrorText>}
        {relationshipSexWarning && !memberErrors?.relationshipToHead?.message && (
          <WarningText>{relationshipSexWarning.message}</WarningText>
        )}
      </DataCell>
      <DataCell
        hasError={Boolean(memberErrors?.sex?.message)}
        hasWarning={Boolean(relationshipSexWarning && !memberErrors?.sex?.message)}
      >
        <CodedInput
          className={cellInput}
          data-grid-input="true"
          questionKey="q03_sex"
          registration={register(`members.${index}.sex`)}
          value={watchedMembers[index]?.sex}
        />
        {memberErrors?.sex?.message && <ErrorText>{memberErrors.sex.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={showReportedAgeError}>
        <AgeEntryCell
          ageAsOfDate={ageReferenceDate}
          detailedAge={detailedAge}
          index={index}
          member={member}
          officialAge={resolvedAge.ageYears}
          register={register}
        />
        {showReportedAgeError && <ErrorText>{memberErrors?.reportedAge?.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={showDateOfBirthError}>
        <DateOfBirthInput
          className={cellInput}
          data-grid-input="true"
          placeholder="MM/DD/YYYY"
          registration={register(`members.${index}.dateOfBirth`)}
          title="Enter complete date as MM/DD/YYYY."
          value={watchedMembers[index]?.dateOfBirth}
          disabled={member?.dateOfBirthUnknown === true}
        />
        <DateOfBirthUnknownControl
          ageReferenceDate={ageReferenceDate}
          clearErrors={clearErrors}
          index={index}
          isUnknown={member?.dateOfBirthUnknown === true}
          register={register}
          resetField={resetField}
          setValue={setValue}
        />
        {showDateOfBirthError && <ErrorText>{memberErrors?.dateOfBirth?.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={Boolean(memberErrors?.placeOfBirth?.message)}>
        <Input
          className={cellInput}
          data-grid-input="true"
          placeholder="City/Municipality, Province"
          {...register(`members.${index}.placeOfBirth`)}
        />
        {memberErrors?.placeOfBirth?.message && <ErrorText>{memberErrors.placeOfBirth.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={Boolean(memberErrors?.nationality?.message || memberErrors?.nationalityOther?.message)}>
        <CodedInput
          className={cellInput}
          data-grid-input="true"
          questionKey="q07_nationality"
          registration={register(`members.${index}.nationality`)}
          value={watchedMembers[index]?.nationality}
        />
        {showNationalityOther ? (
          <Input
            autoUppercase
            className={cn(cellInput, "border-t border-slate-300", memberErrors?.nationalityOther?.message && "bg-red-50")}
            data-grid-input="true"
            placeholder="Specify nationality"
            {...register(`members.${index}.nationalityOther`)}
          />
        ) : null}
        {memberErrors?.nationality?.message && <ErrorText>{memberErrors.nationality.message}</ErrorText>}
        {memberErrors?.nationalityOther?.message && <ErrorText>{memberErrors.nationalityOther.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={Boolean(memberErrors?.civilStatus?.message)}>
        <CodedInput
          className={cellInput}
          data-grid-input="true"
          questionKey="q08_marital_status"
          registration={register(`members.${index}.civilStatus`)}
          value={watchedMembers[index]?.civilStatus}
        />
        {memberErrors?.civilStatus?.message && <ErrorText>{memberErrors.civilStatus.message}</ErrorText>}
      </DataCell>
      <DataCell>
        <Input className={cellInput} data-grid-input="true" {...register(`members.${index}.religion`)} />
      </DataCell>
      <DataCell>
        <Input className={cellInput} data-grid-input="true" {...register(`members.${index}.ethnicity`)} />
      </DataCell>
      <DataCell hasError={Boolean(memberErrors?.highestEducation?.message)}>
        {q11Skipped ? (
          <SkippedInput ariaLabel={`Line ${index + 1} Q11 not applicable`} />
        ) : (
          <CodedInput
            className={cellInput}
            data-grid-input="true"
            questionKey="q11_highest_education"
            registration={register(`members.${index}.highestEducation`)}
            value={watchedMembers[index]?.highestEducation}
          />
        )}
        {!q11Skipped && memberErrors?.highestEducation?.message && <ErrorText>{memberErrors.highestEducation.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={Boolean(memberErrors?.currentlyEnrolled?.message)}>
        {q12Skipped ? (
          <SkippedInput ariaLabel={`Line ${index + 1} Q12 not applicable`} />
        ) : (
          <CodedInput
            className={cellInput}
            data-grid-input="true"
            questionKey="q12_currently_enrolled"
            registration={register(`members.${index}.currentlyEnrolled`)}
            value={watchedMembers[index]?.currentlyEnrolled}
          />
        )}
        {!q12Skipped && memberErrors?.currentlyEnrolled?.message && <ErrorText>{memberErrors.currentlyEnrolled.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={Boolean(memberErrors?.schoolLevel?.message)}>
        {q13Skipped ? (
          <SkippedInput ariaLabel={`Line ${index + 1} Q13 not applicable`} />
        ) : (
          <CodedInput
            className={cellInput}
            data-grid-input="true"
            questionKey="q13_school_level"
            registration={register(`members.${index}.schoolLevel`)}
            value={watchedMembers[index]?.schoolLevel}
          />
        )}
        {!q13Skipped && memberErrors?.schoolLevel?.message && <ErrorText>{memberErrors.schoolLevel.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={Boolean(memberErrors?.placeOfSchool?.message)}>
        {q14Skipped ? (
          <SkippedInput ariaLabel={`Line ${index + 1} Q14 not applicable`} />
        ) : (
          <Input
            className={cellInput}
            data-grid-input="true"
            placeholder="Barangay, City/Municipality"
            {...register(`members.${index}.placeOfSchool`)}
          />
        )}
        {!q14Skipped && memberErrors?.placeOfSchool?.message && <ErrorText>{memberErrors.placeOfSchool.message}</ErrorText>}
      </DataCell>
      <DataCell hasError={Boolean(q15Error || q15OtherError)}>
        {q15State === "applicable" ? (
          <>
            <CodedInput
              className={cellInput}
              data-grid-input="true"
              placeholder="1"
              questionKey="q15_reason_not_attending_school"
              registration={register(`members.${index}.reasonNotAttendingSchool`)}
              value={member?.reasonNotAttendingSchool}
            />
            {!q15.error && q15.code === "20" && !q15HasInlineOther ? (
              <Input
                autoUppercase
                className={cn(cellInput, "border-t border-slate-300", q15OtherError && "bg-red-50")}
                data-grid-input="true"
                placeholder="Specify"
                {...register(`members.${index}.reasonNotAttendingSchoolOther`)}
              />
            ) : null}
            {q15Error ? <ErrorText>{String(q15Error)}</ErrorText> : null}
            {q15OtherError ? <ErrorText>{String(q15OtherError)}</ErrorText> : null}
          </>
        ) : (
          <Input
            aria-label={`Line ${index + 1} Q15 not applicable`}
            className={cn(cellInput, "text-center font-semibold text-slate-600")}
            readOnly
            tabIndex={-1}
            value={q15State === "notApplicable" ? "99 - N/A" : ""}
          />
        )}
      </DataCell>
    </tr>
  );
}

function FamilyAssignmentEditor({
  errors,
  family,
  fields,
  register,
  setValue,
}: {
  errors: FieldErrors<AddNewDataFormValues>;
  family: AddNewDataFormValues["household"]["family"];
  fields: FieldArrayWithId<AddNewDataFormValues, "members", "id">[];
  register: UseFormRegister<AddNewDataFormValues>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
}) {
  const requiresFamilyHouseholdRelationship = family !== "A";

  useEffect(() => {
    if (requiresFamilyHouseholdRelationship) {
      return;
    }

    setValue(
      "household.familyRelationshipToHouseholdHead",
      "",
      {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      },
    );
  }, [requiresFamilyHouseholdRelationship, setValue]);

  /*
   * Family Assignment applies only to Family B and succeeding
   * families. Family A is the primary household family and
   * must not display this section.
   */
  if (!requiresFamilyHouseholdRelationship) {
    return null;
  }

  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm">
      <div className="mb-3">
        <p className="text-sm font-semibold text-slate-950">
          Family Assignment
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Specify the family's relationship to the Household Head and identify the family head.
        </p>
      </div>

      {requiresFamilyHouseholdRelationship ? (
        <div
          className={cn(
            "mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3",
            errors.household?.familyRelationshipToHouseholdHead?.message &&
              "border-red-400 bg-red-50",
          )}
        >
          <label className="text-xs font-semibold text-slate-700">
            {`What is the relationship of Family ${family} to the Household Head?`}
            <Input
              autoUppercase
              className="mt-1 h-9 bg-white"
              placeholder="Relationship to Household Head"
              {...register(
                "household.familyRelationshipToHouseholdHead",
              )}
            />
          </label>

          {errors.household?.familyRelationshipToHouseholdHead?.message ? (
            <p className="mt-1 text-xs font-semibold text-red-700">
              {String(
                errors.household.familyRelationshipToHouseholdHead.message,
              )}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-2">
        {fields.slice(0, 1).map((field, index) => {
          const memberErrors = errors.members?.[index];

          return (
            <div
              key={field.id}
              className={cn(
                "grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[minmax(0,1fr)_auto]",
                memberErrors?.isFamilyHead?.message &&
                  "border-red-400 bg-red-50",
              )}
            >

              <label className="flex items-center gap-2 self-end pb-2 text-xs font-semibold text-slate-700">
                <input
                  type="checkbox"
                  {...register(
                    `members.${index}.isFamilyHead`,
                  )}
                />
                Family Head
              </label>

              {memberErrors?.isFamilyHead?.message ? (
                <p className="text-xs font-semibold text-red-700 sm:col-span-2">
                  {String(
                    memberErrors.isFamilyHead.message,
                  )}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function isQuestionSkipped(
  member: AddNewDataFormValues["members"][number] | undefined,
  field: MemberFieldName,
) {
  if (!member) {
    return false;
  }

  return getApplicabilityForQuestion(member, field) === "notApplicable";
}

function SkippedInput({ ariaLabel }: { ariaLabel: string }) {
  return (
    <Input
      aria-label={ariaLabel}
      className={cn(cellInput, "text-center font-semibold text-slate-600")}
      readOnly
      tabIndex={-1}
      value="99 - N/A"
    />
  );
}

function getReasonNotAttendingState(member: AddNewDataFormValues["members"][number] | undefined) {
  if (!member) {
    return "pending";
  }

  const enrollment = cleanRBIMCode("q12_currently_enrolled", member.currentlyEnrolled);

  if (!enrollment.code || enrollment.error) {
    return getApplicabilityForQuestion(member, "currentlyEnrolled") === "applicable"
      ? "pending"
      : "notApplicable";
  }

  if (getApplicabilityForQuestion(member, "reasonNotAttendingSchool") === "applicable") {
    return "applicable";
  }

  return "notApplicable";
}

function QuestionHeader({
  code,
  title,
  children,
  className,
}: {
  code: string;
  title: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <th className={cn("border border-slate-700 bg-[#eeeeee] px-2 py-2 align-middle font-bold uppercase", className)}>
      <span className="block">{code}</span>
      <span className="block leading-tight">{title}</span>
      {children && <span className="mt-1 block text-[11px] font-semibold normal-case leading-tight">{children}</span>}
    </th>
  );
}

function Instruction({ children }: { children: ReactNode }) {
  return (
    <th className="border border-slate-700 bg-[#eeeeee] px-2 py-2 align-top text-[11px] font-medium leading-tight">
      {children}
    </th>
  );
}

function DataCell({
  children,
  hasError = false,
  hasWarning = false,
}: {
  children: ReactNode;
  hasError?: boolean;
  hasWarning?: boolean;
}) {
  return (
    <td
      className={cn(
        "border border-slate-700 bg-white align-top",
        hasError && "bg-destructive/5 ring-1 ring-inset ring-destructive",
        !hasError && hasWarning && "bg-amber-50/70 ring-1 ring-inset ring-amber-400",
      )}
    >
      {children}
    </td>
  );
}

function formatQ4QuestionnaireAge(
  dateOfBirth: string | undefined,
  ageAsOfDate: string,
  officialAge: number | null,
  hasExactBirthdate: boolean,
) {
  if (officialAge === null) {
    return "Not yet computed";
  }

  const birthDate = dateOfBirth
    ? parseMMDDYYYY(dateOfBirth)
    : null;

  const referenceDate =
    parseMMDDYYYY(ageAsOfDate);

  /*
   * If a usable DOB/reference date is unavailable, keep the
   * canonical completed-year age but format it to 2 decimals.
   */
  if (!birthDate || !referenceDate) {
    return `${officialAge.toFixed(2)} years`;
  }

  let completedMonths =
    (referenceDate.getFullYear() -
      birthDate.getFullYear()) *
      12 +
    (referenceDate.getMonth() -
      birthDate.getMonth());

  if (
    referenceDate.getDate() <
    birthDate.getDate()
  ) {
    completedMonths -= 1;
  }

  completedMonths =
    Math.max(0, completedMonths);

  /*
   * Partial/legacy DOB values must not invent day-level
   * precision. For an infant, however, Q4 still displays
   * the available completed months instead of years.
   */
  if (!hasExactBirthdate) {
    if (completedMonths < 12) {
      return `${completedMonths.toFixed(2)} months`;
    }

    return `${(
      completedMonths / 12
    ).toFixed(2)} years`;
  }

  /*
   * Exact DOB:
   * calculate the fractional part of the current calendar
   * month so very young infants can display values such as
   * 0.50 months.
   */
  const currentAnniversary =
    addQ4CalendarMonths(
      birthDate,
      completedMonths,
    );

  const nextAnniversary =
    addQ4CalendarMonths(
      birthDate,
      completedMonths + 1,
    );

  const referenceStamp = Date.UTC(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );

  const currentStamp = Date.UTC(
    currentAnniversary.getFullYear(),
    currentAnniversary.getMonth(),
    currentAnniversary.getDate(),
  );

  const nextStamp = Date.UTC(
    nextAnniversary.getFullYear(),
    nextAnniversary.getMonth(),
    nextAnniversary.getDate(),
  );

  const monthDuration =
    nextStamp - currentStamp;

  const elapsed =
    Math.max(
      0,
      referenceStamp - currentStamp,
    );

  const fractionalMonth =
    monthDuration > 0
      ? Math.min(
          1,
          elapsed / monthDuration,
        )
      : 0;

  const calculatedMonths =
    completedMonths + fractionalMonth;

  if (calculatedMonths < 12) {
    return `${calculatedMonths.toFixed(2)} months`;
  }

  return `${(
    calculatedMonths / 12
  ).toFixed(2)} years`;
}

function addQ4CalendarMonths(
  source: Date,
  monthsToAdd: number,
) {
  const rawMonth =
    source.getMonth() + monthsToAdd;

  const targetYear =
    source.getFullYear() +
    Math.floor(rawMonth / 12);

  const targetMonth =
    ((rawMonth % 12) + 12) % 12;

  const lastDay =
    new Date(
      targetYear,
      targetMonth + 1,
      0,
    ).getDate();

  return new Date(
    targetYear,
    targetMonth,
    Math.min(
      source.getDate(),
      lastDay,
    ),
  );
}
function formatInfantQ4Age(
  dateOfBirth: string | undefined,
  ageAsOfDate: string,
) {
  if (!dateOfBirth) {
    return null;
  }

  const birthDate =
    parseMMDDYYYY(dateOfBirth);

  const referenceDate =
    parseMMDDYYYY(ageAsOfDate);

  if (!birthDate || !referenceDate) {
    return null;
  }

  if (referenceDate < birthDate) {
    return null;
  }

  let completedMonths =
    (referenceDate.getFullYear() -
      birthDate.getFullYear()) *
      12 +
    (referenceDate.getMonth() -
      birthDate.getMonth());

  if (
    referenceDate.getDate() <
    birthDate.getDate()
  ) {
    completedMonths -= 1;
  }

  completedMonths =
    Math.max(0, completedMonths);

  if (completedMonths >= 12) {
    return null;
  }

  const currentAnniversary =
    addQ4Months(
      birthDate,
      completedMonths,
    );

  const nextAnniversary =
    addQ4Months(
      birthDate,
      completedMonths + 1,
    );

  const currentStamp = Date.UTC(
    currentAnniversary.getFullYear(),
    currentAnniversary.getMonth(),
    currentAnniversary.getDate(),
  );

  const nextStamp = Date.UTC(
    nextAnniversary.getFullYear(),
    nextAnniversary.getMonth(),
    nextAnniversary.getDate(),
  );

  const referenceStamp = Date.UTC(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );

  const monthLength =
    nextStamp - currentStamp;

  const elapsed =
    Math.max(
      0,
      referenceStamp - currentStamp,
    );

  const fraction =
    monthLength > 0
      ? Math.min(
          1,
          elapsed / monthLength,
        )
      : 0;

  const ageInMonths =
    completedMonths + fraction;

  return `${ageInMonths.toFixed(2)} months`;
}

function addQ4Months(
  source: Date,
  months: number,
) {
  const rawMonth =
    source.getMonth() + months;

  const year =
    source.getFullYear() +
    Math.floor(rawMonth / 12);

  const month =
    ((rawMonth % 12) + 12) % 12;

  const lastDay =
    new Date(
      year,
      month + 1,
      0,
    ).getDate();

  return new Date(
    year,
    month,
    Math.min(
      source.getDate(),
      lastDay,
    ),
  );
}
function AgeEntryCell({
  ageAsOfDate,
  detailedAge,
  index,
  member,
  officialAge,
  register,
}: {
  ageAsOfDate: string;
  detailedAge: string | null;
  index: number;
  member: AddNewDataFormValues["members"][number] | undefined;
  officialAge: number | null;
  register: UseFormRegister<AddNewDataFormValues>;
}) {
  if (member?.dateOfBirthUnknown) {
    return (
      <div className="space-y-1 px-2 py-1.5">
        <Input
          aria-label={`Reported age for household member ${index + 1}`}
          className={cellInput}
          data-grid-input="true"
          inputMode="numeric"
          max={120}
          min={0}
          placeholder="0-120"
          step={1}
          type="number"
          {...register(`members.${index}.reportedAge`, {
            setValueAs: (value) => String(value ?? "").trim() === "" ? null : Number(value),
          })}
          onKeyDown={preventInvalidAgeKey}
          onPaste={preventInvalidAgePaste}
        />
        <p className="px-1 text-left text-[10px] leading-snug text-slate-600">
          Reported age as of {formatAgeAsOfDate(ageAsOfDate) || ageAsOfDate}.
        </p>
      </div>
    );
  }

  const infantDisplay =
    officialAge === 0
      ? formatQ4QuestionnaireAge(
          member?.dateOfBirth,
          ageAsOfDate,
          officialAge,
          detailedAge !== null,
        )
      : null;

  const q4Display = (() => {
    /*
     * Q4 rule:
     * 0-11 months -> decimal months with exactly 2 decimals.
     * 12+ months   -> normal years/months display.
     */
    if (infantDisplay) {
      return infantDisplay;
    }

    if (detailedAge) {
      const ageParts = detailedAge.match(
        /^(\d+)\s+years?,\s*(\d+)\s+months?$/i,
      );

      if (ageParts) {
        const years = Number(ageParts[1]);
        const months = Number(ageParts[2]);

        if (months > 0) {
          return `${years} years ${months} months`;
        }

        return `${years} ${years === 1 ? "year" : "years"}`;
      }

      /*
       * Some exact ages may already be formatted without a
       * remaining-month component.
       */
      return detailedAge.replace(",", "");
    }

    return officialAge === null
      ? "Not yet computed"
      : `${officialAge} ${officialAge === 1 ? "year" : "years"}`;
  })();

  return (
    <div className="px-2 py-1.5">
      <div className="min-h-[58px] rounded-md border border-emerald-100 bg-emerald-50/80 px-2.5 py-2 text-left">
        <div className="text-[10px] font-semibold uppercase tracking-normal text-emerald-800">Q4 Age</div>
        <div className={cn("mt-1 text-sm font-semibold leading-5", officialAge !== null ? "text-slate-950" : "text-slate-500")}>
          {q4Display}
        </div>
      </div>
    </div>
  );
}

function DateOfBirthUnknownControl({
  ageReferenceDate,
  clearErrors,
  index,
  isUnknown,
  register,
  resetField,
  setValue,
}: {
  ageReferenceDate: string;
  clearErrors: UseFormClearErrors<AddNewDataFormValues>;
  index: number;
  isUnknown: boolean;
  register: UseFormRegister<AddNewDataFormValues>;
  resetField: UseFormResetField<AddNewDataFormValues>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
}) {
  const registration = register(`members.${index}.dateOfBirthUnknown`);

  return (
    <label className="flex cursor-pointer items-center gap-1.5 px-2 py-1.5 text-left text-[10px] font-medium text-slate-700">
      <input
        {...registration}
        checked={isUnknown}
        className="size-3.5 accent-emerald-700"
        type="checkbox"
          onChange={(event) => {
            void registration.onChange(event);
            setAgeEntryMode(
              index,
              event.currentTarget.checked,
              ageReferenceDate,
              clearErrors,
              resetField,
              setValue,
            );
          }}
      />
      Date of Birth Unknown
    </label>
  );
}

function setAgeEntryMode(
  index: number,
  dateOfBirthUnknown: boolean,
  ageReferenceDate: string,
  clearErrors: UseFormClearErrors<AddNewDataFormValues>,
  resetField: UseFormResetField<AddNewDataFormValues>,
  setValue: UseFormSetValue<AddNewDataFormValues>,
) {
  const options = { shouldDirty: true, shouldTouch: true, shouldValidate: false } as const;
  const modeValues = getAgeEntryModeValues(dateOfBirthUnknown, ageReferenceDate);
  setValue(`members.${index}.dateOfBirthUnknown`, modeValues.dateOfBirthUnknown, options);
  setValue(`members.${index}.birthdateSource`, modeValues.birthdateSource, options);

  if (dateOfBirthUnknown) {
    setValue(`members.${index}.dateOfBirth`, modeValues.dateOfBirth ?? "", options);
    setValue(`members.${index}.birthdateOriginalValue`, modeValues.birthdateOriginalValue ?? "", options);
    setValue(`members.${index}.birthYear`, modeValues.birthYear ?? null, options);
    setValue(`members.${index}.birthMonth`, modeValues.birthMonth ?? null, options);
    setValue(`members.${index}.birthDay`, modeValues.birthDay ?? null, options);
    setValue(`members.${index}.birthdatePrecision`, modeValues.birthdatePrecision, options);
    setValue(`members.${index}.ageSource`, modeValues.ageSource, options);
    setValue(`members.${index}.ageAsOfDate`, modeValues.ageAsOfDate, options);
    resetField(`members.${index}.dateOfBirth`, {
      defaultValue: "",
      keepDirty: true,
    });
    clearErrors(`members.${index}.dateOfBirth`);
    return;
  }

  setValue(`members.${index}.reportedAge`, modeValues.reportedAge ?? null, options);
  setValue(`members.${index}.ageSource`, modeValues.ageSource, options);
  setValue(`members.${index}.ageAsOfDate`, modeValues.ageAsOfDate, options);
  setValue(`members.${index}.birthdatePrecision`, modeValues.birthdatePrecision, options);
  resetField(`members.${index}.reportedAge`, {
    defaultValue: null,
    keepDirty: true,
  });
  clearErrors(`members.${index}.reportedAge`);
}

function preventInvalidAgeKey(event: KeyboardEvent<HTMLInputElement>) {
  if (["-", "+", ".", ",", "e", "E"].includes(event.key)) {
    event.preventDefault();
  }
}

function preventInvalidAgePaste(event: ClipboardEvent<HTMLInputElement>) {
  if (!/^\d{1,3}$/.test(event.clipboardData.getData("text").trim())) {
    event.preventDefault();
  }
}

function ErrorText({ children }: { children: ReactNode }) {
  return <p className="px-1 pb-1 text-left text-[10px] font-semibold text-destructive">{children}</p>;
}

function WarningText({ children }: { children: ReactNode }) {
  return <p className="px-1 pb-1 text-left text-[10px] font-semibold text-amber-700">{children}</p>;
}

function handleDemographicGridKeyDown(event: KeyboardEvent<HTMLTableElement>) {
  if (event.key !== "Enter" || !(event.target instanceof HTMLInputElement)) {
    return;
  }

  if (event.target.dataset.gridInput !== "true") {
    return;
  }

  event.preventDefault();

  const gridInputs = Array.from(
    event.currentTarget.querySelectorAll<HTMLInputElement>('input[data-grid-input="true"]:not(:disabled)'),
  );
  const currentIndex = gridInputs.indexOf(event.target);
  const nextInput = gridInputs[currentIndex + 1];

  if (!nextInput) {
    return;
  }

  nextInput.focus();
  nextInput.select();
}
